import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { RelatedBlogs } from './BlogIntegrated';

const ServicePage = () => {
  const { categorySlug, subSlug } = useParams();
  const [service, setService] = useState(null);
  const [subService, setSubService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchService = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/cms/service-menu/by-slug/${categorySlug}`
        );
        
        if (!response.ok) throw new Error('Service not found');
        
        const data = await response.json();
        setService(data);
        
        if (subSlug && data.sub_items) {
          const sub = data.sub_items.find(item => item.slug === subSlug);
          setSubService(sub || null);
        } else {
          setSubService(null);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [categorySlug, subSlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#2a2a2a]" />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Service Not Found</h1>
        <p className="text-gray-600 mb-8 text-lg">The service you're looking for doesn't exist.</p>
        <Button asChild>
          <Link to="/services">View All Services</Link>
        </Button>
      </div>
    );
  }

  const displayContent = subService || service;
  const pageTitle = subService ? subService.name : service.name;
  const heroTitle = displayContent.hero_title || pageTitle;
  const heroSubtitle = displayContent.hero_subtitle || displayContent.description || '';
  const heroImage = displayContent.hero_image || displayContent.image || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200';
  const features = displayContent.features || [];
  const benefits = displayContent.benefits || [];
  const content = displayContent.content || '';
  const contentImage = displayContent.content_image || '';

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={`${pageTitle} | Navaris Digital`}
        description={heroSubtitle}
      />

      {/* Hero Section */}
      <section className="relative min-h-[65vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={heroImage} 
            alt={pageTitle}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2a2a2a]/95 via-[#2a2a2a]/85 to-[#2a2a2a]/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10 py-28">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-white/70 text-base mb-3"
          >
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight size={16} />
            <Link to="/services" className="hover:text-white transition-colors">Services</Link>
            <ChevronRight size={16} />
            {subService ? (
              <>
                <Link to={`/services/${categorySlug}`} className="hover:text-white transition-colors">
                  {service.name}
                </Link>
                <ChevronRight size={16} />
                <span className="text-white">{subService.name}</span>
              </>
            ) : (
              <span className="text-white">{service.name}</span>
            )}
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 max-w-3xl"
          >
            {heroTitle}
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl md:text-2xl text-white/85 max-w-2xl mb-6"
          >
            {heroSubtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Button asChild size="lg" className="bg-white text-[#2a2a2a] hover:bg-gray-100 text-base px-8">
              <Link to="/contact">
                Get Started <ArrowRight className="ml-2" size={18} />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-10 lg:py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
            
            {/* Left Side - Main Content */}
            <div className="flex-1 lg:max-w-[68%]">
              
              {/* News-style Content with Image */}
              {(content || contentImage) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="mb-8"
                >
                  <h2 className="text-2xl md:text-3xl font-bold text-[#2a2a2a] mb-4">About {pageTitle} at Navaris Digital</h2>
                  
                  <div className="text-gray-700 leading-relaxed text-base md:text-lg">
                    {contentImage && (
                      <div className="float-left mr-6 mb-4 w-[280px] md:w-[320px]">
                        <div className="relative rounded-xl overflow-hidden shadow-lg">
                          <img 
                            src={contentImage} 
                            alt={`${pageTitle} illustration`}
                            className="w-full h-auto object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#2a2a2a]/20 to-transparent" />
                        </div>
                      </div>
                    )}
                    <div className="whitespace-pre-line">
                      {content || 'Comprehensive solutions tailored to your business needs.'}
                    </div>
                    <div className="clear-both"></div>
                  </div>
                </motion.div>
              )}

              {/* Compact Features & Benefits Row */}
              {(features.length > 0 || benefits.length > 0) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  {/* Features */}
                  {features.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h3 className="text-xl font-bold text-[#2a2a2a] mb-3">Key Features</h3>
                      <ul className="space-y-2">
                        {features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2 text-gray-700 text-base">
                            <CheckCircle className="text-[#2a2a2a] flex-shrink-0 mt-0.5" size={18} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Benefits */}
                  {benefits.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h3 className="text-xl font-bold text-[#2a2a2a] mb-3">Benefits</h3>
                      <ul className="space-y-2">
                        {benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start gap-2 text-gray-700 text-base">
                            <span className="w-5 h-5 bg-[#2a2a2a] rounded text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-medium">
                              {index + 1}
                            </span>
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </motion.div>
              )}
            </div>

            {/* Right Side - Sticky Sidebar */}
            <div className="lg:w-[32%]">
              <div className="lg:sticky lg:top-24 space-y-4">
                
                {/* Sub-services Navigation */}
                {!subService && service.sub_items && service.sub_items.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="bg-gray-50 rounded-xl p-4 border border-gray-100"
                  >
                    <h3 className="font-bold text-[#2a2a2a] mb-3 text-lg">Our Services</h3>
                    <div className="space-y-1.5">
                      {service.sub_items.map((sub, index) => (
                        <Link
                          key={sub.id}
                          to={`/services/${categorySlug}/${sub.slug}`}
                          className="flex items-center justify-between p-2.5 bg-white rounded-lg hover:bg-[#2a2a2a] hover:text-white transition-all group border border-gray-100 hover:border-[#2a2a2a] text-sm"
                        >
                          <span className="font-medium">{sub.name}</span>
                          <ArrowRight size={14} className="text-gray-400 group-hover:text-white group-hover:translate-x-1 transition-all" />
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* CTA Card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-[#2a2a2a] rounded-xl p-4 text-white"
                >
                  <h3 className="text-lg font-bold mb-2">Ready to Get Started?</h3>
                  <p className="text-white/80 mb-4 text-sm leading-relaxed">
                    Let's discuss how we can help transform your business.
                  </p>
                  <Button asChild className="w-full bg-white text-[#2a2a2a] hover:bg-gray-100 font-semibold text-sm h-9">
                    <Link to="/contact" className="flex items-center justify-center gap-2">
                      Contact Us
                      <ArrowRight size={14} />
                    </Link>
                  </Button>
                </motion.div>

                {/* Back to parent */}
                {subService && (
                  <Link
                    to={`/services/${categorySlug}`}
                    className="flex items-center gap-2 text-[#2a2a2a] hover:text-gray-600 transition-colors font-medium p-3 bg-gray-50 rounded-lg text-sm"
                  >
                    <ChevronRight size={16} className="rotate-180" />
                    Back to {service.name}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Blog Posts */}
      {service && (
        <RelatedBlogs 
          serviceIds={[service.id]} 
          title={`Insights on ${subService?.name || service.name}`}
          limit={3}
        />
      )}

      {/* CTA Section */}
      <section className="py-10 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#2a2a2a] mb-3">
            Let's Build Something Great Together
          </h2>
          <p className="text-gray-600 mb-5 max-w-2xl mx-auto text-base">
            Partner with Navaris Digital for innovative solutions that drive real business results.
          </p>
          <Button asChild size="lg" className="bg-[#2a2a2a] hover:bg-[#3a3a3a] text-base">
            <Link to="/contact" className="flex items-center gap-2">
              Start Your Project
              <ArrowRight size={18} />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
